import { lazy, type ReactNode } from "react";
import { Navigate, createBrowserRouter } from "react-router-dom";

import AdminLayout from "@/layout";
import { RequirePermission } from "@/layout/require-permission";
import { LEGACY_FINANCE_PATHS, PATHS } from "@/constants";

// Har sahifa alohida chunk: admin panelga birinchi kirishda faqat
// dashboard yuklanadi, ledger jadvali emas.
const DashboardPage = lazy(() => import("@/pages/dashboard"));
const UsersPage = lazy(() => import("@/pages/users"));
const UserDetailPage = lazy(() => import("@/pages/users/user-detail"));
const InvitationsPage = lazy(() => import("@/pages/invitations"));
const PromoPage = lazy(() => import("@/pages/promo"));
const LivePage = lazy(() => import("@/pages/live"));
const ReviewsPage = lazy(() => import("@/pages/reviews"));
const MessagesPage = lazy(() => import("@/pages/messages"));
const PayoutsPage = lazy(() => import("@/pages/payouts"));
const InvitationDetailPage = lazy(() => import("@/pages/invitations/invitation-detail"));
const LedgerPage = lazy(() => import("@/pages/ledger"));
const PaymentsPage = lazy(() => import("@/pages/payments"));
const WithdrawalsPage = lazy(() => import("@/pages/withdrawals"));
const ReportsPage = lazy(() => import("@/pages/reports"));
const ReportDetailPage = lazy(() => import("@/pages/reports/report-detail"));
const HostApplicationsPage = lazy(() => import("@/pages/host-applications"));
const HostApplicationDetailPage = lazy(
  () => import("@/pages/host-applications/application-detail"),
);
const SettingsPage = lazy(() => import("@/pages/settings"));
const MeetingOptionsPage = lazy(() => import("@/pages/settings/meeting-options"));
const RolesPage = lazy(() => import("@/pages/roles"));
const RoleFormPage = lazy(() => import("@/pages/roles/role-form"));
const AuditPage = lazy(() => import("@/pages/audit"));
const IndustriesPage = lazy(() => import("@/pages/reference/industries"));
const RegionsPage = lazy(() => import("@/pages/reference/regions"));
const DistrictsPage = lazy(() => import("@/pages/reference/districts"));
const AnalyticsPage = lazy(() => import("@/pages/analytics"));
const NotFoundPage = lazy(() => import("@/pages/not-found"));

/**
 * Xato ekrani `lazy()` BILAN EMAS — ataylab.
 *
 * Uning eng ko'p uchraydigan sababi aynan bo'lak yuklanmasligi
 * (eski build). Xato ekranining o'zi ham bo'lak bilan kelsa, u ham
 * yuklanmasdi — va odam yana oq ekranda qolardi.
 */
import RouteErrorPage from "@/pages/route-error";

const guard = (permission: string, element: ReactNode) => (
  <RequirePermission permission={permission}>{element}</RequirePermission>
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: <AdminLayout />,
    // Router'da `errorElement` umuman yo'q edi: har qanday render
    // xatosi React Router'ning xom ekranini ko'rsatardi — texnik
    // stack bilan, panel korpusisiz.
    errorElement: <RouteErrorPage />,
    children: [
      // Har bir marshrut o'z ruxsati bilan (ADR 0009). Menyudan
      // yashiringan sahifaga manzil orqali kirish mumkin — bu qorovul
      // aynan o'sha holat uchun.
      { index: true, element: guard("dashboard.read", <DashboardPage />) },
      { path: PATHS.USERS, element: guard("users.read", <UsersPage />) },
      // Kartochka alohida ruxsat bilan: ro'yxatni ko'rish huquqi
      // profilni ochish huquqini anglatmaydi (ADR 0009).
      { path: PATHS.USER_DETAIL, element: guard("users.detail.read", <UserDetailPage />) },
      { path: PATHS.INVITATIONS, element: guard("invitations.read", <InvitationsPage />) },
      // Uchrashuv kartochkasi ro'yxat bilan BIR XIL ruxsat ostida:
      // qatordagi hamma narsa kartochkada ham bor. Jurnal va ishtirok
      // esa sahifaning ichida o'z ruxsati bilan qo'riqlanadi
      // (`reports.read`) — ular boshqa savolga javob beradi va boshqa
      // huquq talab qiladi.
      {
        path: PATHS.INVITATION_DETAIL,
        element: guard("invitations.read", <InvitationDetailPage />),
      },
      // `/finance` o'zi sahifa emas: menyudagi bo'lim — `/reference`
      // bilan bir xil. Bo'limning o'ziga tushgan odam hisobga boradi:
      // uchtasidan eng to'lig'i o'sha.
      { path: PATHS.FINANCE, element: <Navigate to={PATHS.LEDGER} replace /> },
      { path: PATHS.LEDGER, element: guard("ledger.read", <LedgerPage />) },
      { path: PATHS.PAYMENTS, element: guard("payments.read", <PaymentsPage />) },
      { path: PATHS.WITHDRAWALS, element: guard("payments.read", <WithdrawalsPage />) },
      { path: PATHS.PROMO, element: guard("promo.read", <PromoPage />) },
      { path: PATHS.PAYOUTS, element: guard("payouts.read", <PayoutsPage />) },

      // Eski manzillar. Xatcho'p va tashqi havolalar (masalan
      // hisobotdagi link) yo'qolib qolmasin — `replace` bilan, ya'ni
      // "orqaga" tugmasi eski manzilga qaytarib halqa hosil qilmaydi.
      ...Object.entries(LEGACY_FINANCE_PATHS).map(([from, to]) => ({
        path: from,
        element: <Navigate to={to} replace />,
      })),
      { path: PATHS.LIVE, element: guard("live.manage", <LivePage />) },
      { path: PATHS.REPORTS, element: guard("reports.read", <ReportsPage />) },
      { path: PATHS.REVIEWS, element: guard("reviews.read", <ReviewsPage />) },
      { path: PATHS.MESSAGES, element: guard("notifications.send", <MessagesPage />) },
      // Kartochka alohida sahifada: nizoni ko'rish uchun bir vaqtda
      // to'rt manba kerak (tomonlar, yozishmalar, xona jurnali,
      // uchrashuv chati), va ular yon panelga sig'masdi.
      { path: PATHS.REPORT_DETAIL, element: guard("reports.read", <ReportDetailPage />) },
      {
        path: PATHS.HOST_APPLICATIONS,
        element: guard("hosts.applications.read", <HostApplicationsPage />),
      },
      {
        path: PATHS.HOST_APPLICATION_DETAIL,
        element: guard("hosts.applications.read", <HostApplicationDetailPage />),
      },
      { path: PATHS.ANALYTICS, element: guard("analytics.read", <AnalyticsPage />) },
      { path: PATHS.ROLES, element: guard("roles.read", <RolesPage />) },
      // Yaratish `/roles/:id` dan OLDIN: aks holda "new" so'zi rol
      // id'si sifatida qabul qilinardi.
      { path: PATHS.ROLE_CREATE, element: guard("roles.create", <RoleFormPage />) },
      { path: PATHS.ROLE_DETAIL, element: guard("roles.read", <RoleFormPage />) },
      { path: PATHS.AUDIT, element: guard("audit.read", <AuditPage />) },
      // `/reference` o'zi sahifa emas: menyudagi bo'lim. Eski havolalar
      // va xatcho'plar birinchi bo'limga tushadi.
      { path: PATHS.REFERENCE, element: <Navigate to={PATHS.REFERENCE_INDUSTRIES} replace /> },
      {
        path: PATHS.REFERENCE_INDUSTRIES,
        element: guard("reference.read", <IndustriesPage />),
      },
      { path: PATHS.REFERENCE_REGIONS, element: guard("reference.read", <RegionsPage />) },
      { path: PATHS.REFERENCE_DISTRICTS, element: guard("reference.read", <DistrictsPage />) },
      // `/settings` o'zi sahifa emas: menyudagi bo'lim — `/reference`
      // va `/finance` bilan bir xil. Eski xatcho'plar platforma
      // raqamlariga tushadi: bo'lim shundan boshlangan edi.
      { path: PATHS.SETTINGS, element: <Navigate to={PATHS.SETTINGS_PLATFORM} replace /> },
      { path: PATHS.SETTINGS_PLATFORM, element: guard("settings.read", <SettingsPage />) },
      { path: PATHS.SETTINGS_MEETINGS, element: guard("settings.read", <MeetingOptionsPage />) },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
